import { inject, Injectable } from '@angular/core';
import { createEffect } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  startWith
} from 'rxjs/operators';
import { zefDialogOpen } from '@zerops/zef/dialog';
import { selectAuthState, ZefAuthState } from '@zerops/zef/auth';
import { UserEntity, selectUserBaseActiveClientUserId } from '@zerops/zerops/core/user-base';
import { FEATURE_NAME } from './client-user-role-dialog.constant';

interface RoleState {
  clientUserId: string;
  roleCode: string;
}

/**
 * Effect that detects role changes for the active client user and opens
 * a notification dialog when the role changes during an active session.
 *
 * Edge cases handled:
 * - Initial login: No dialog (first role emission after login)
 * - Page refresh: No dialog (restoring previous session)
 * - Client switch: No dialog (changing to different client's role)
 * - Logout/login: No dialog on login (new session = new initial state)
 * - Role change via WebSocket: Opens dialog (actual role change)
 * - User deletion/deactivation: Filtered out (no valid client user)
 * - WebSocket reconnect: No duplicate dialog (same role = no change)
 *
 * The key insight is tracking (clientUserId, roleCode) pairs:
 * - When clientUserId changes: New context, don't show dialog
 * - When roleCode changes for SAME clientUserId: Show dialog
 */
@Injectable()
export class ClientUserRoleDialogEffect {
  #store = inject(Store);
  #userEntity = inject(UserEntity);

  // Stream of valid role states (authenticated user with active client)
  #roleState$ = combineLatest([
    this.#store.pipe(select(selectAuthState)),
    this.#store.pipe(select(selectUserBaseActiveClientUserId)),
    this.#userEntity.activeClientUser$
  ]).pipe(
    // Only proceed when authenticated and have valid data
    filter(([ authState, clientUserId, clientUser ]) =>
      authState === ZefAuthState.Authorized &&
      !!clientUserId &&
      !!clientUser?.roleCode
    ),
    // Extract relevant data
    map(([ , clientUserId, clientUser ]): RoleState => ({
      clientUserId,
      roleCode: clientUser.roleCode
    })),
    // Dedupe identical emissions
    distinctUntilChanged((prev, curr) =>
      prev.clientUserId === curr.clientUserId && prev.roleCode === curr.roleCode
    )
  );

  /**
   * Detects actual role changes during an active session.
   *
   * Uses pairwise() to compare consecutive (clientUserId, roleCode) pairs.
   * Only triggers when:
   * 1. User is authenticated
   * 2. Both previous and current states have the same clientUserId
   * 3. The roleCode has changed
   *
   * This correctly handles:
   * - Login: prev=null, curr={id, role} → clientUserId mismatch → no dialog
   * - Client switch: prev={id1, role1}, curr={id2, role2} → clientUserId mismatch → no dialog
   * - Role change: prev={id, role1}, curr={id, role2} → same clientUserId, different role → dialog
   * - Logout: curr becomes null → filtered out before pairwise
   */
  onActiveClientUserRoleUpdate$ = createEffect(() => this.#roleState$.pipe(
    // Start with null to ensure pairwise has a baseline for first emission
    startWith(null as RoleState | null),
    // Compare consecutive emissions
    pairwise(),
    // Only trigger when:
    // 1. We have both previous and current values (not initial emission)
    // 2. The clientUserId is the same (not a client switch or login)
    // 3. The roleCode has changed
    filter(([ prev, curr ]) =>
      prev !== null &&
      curr !== null &&
      prev.clientUserId === curr.clientUserId &&
      prev.roleCode !== curr.roleCode
    ),
    map(() => zefDialogOpen({ key: FEATURE_NAME }))
  ));
}
